import type { ReactNode } from "react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableDropdown } from "@/components/ui/searchable-dropdown";

export const ChartFooterRow = ({ children }: { children: ReactNode }) => (
  <div className="flex w-full min-w-0 flex-nowrap items-center justify-end gap-x-3 px-6 pb-6">
    {children}
  </div>
);

export const FooterLabeledSelect = <T extends string>({
  label,
  value,
  onValueChange,
  items,
}: {
  label: string;
  value: T;
  onValueChange: (value: T) => void;
  items: { value: T; label: string }[];
}) => (
  <div className="flex min-w-0 flex-[0_1_auto] items-center gap-1.5">
    <span className="shrink-0 text-xs text-gray-600">{label}:</span>
    <div className="w-[128px] min-w-0 max-w-[128px] shrink">
      <Select value={value} onValueChange={v => onValueChange(v as T)}>
        <SelectTrigger className="h-[30px] w-full min-w-0 px-2 py-1 text-xs [&>span:first-child]:block [&>span:first-child]:min-w-0 [&>span:first-child]:truncate">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {items.map((item: { value: T; label: string }) => (
            <SelectItem key={item.value} value={item.value} className="text-xs">
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  </div>
);

interface FooterSearchableSelectItem {
  id: string;
  label: string;
}

interface FooterSearchableSelectProps {
  label: string;
  selectedItem: FooterSearchableSelectItem;
  onSelect: (itemId: string) => void;
  categories: {
    label: string;
    items: FooterSearchableSelectItem[];
  }[];
  onSearchChange?: (query: string) => void;
  onOpenChange?: (open: boolean) => void;
  isLoading?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
}

export const FooterSearchableSelect = ({
  label,
  selectedItem,
  onSelect,
  categories,
  onSearchChange,
  onOpenChange,
  isLoading = false,
  searchPlaceholder = "Search...",
  emptyMessage = "No results found.",
}: FooterSearchableSelectProps) => (
  <div className="flex min-w-0 flex-[0_1_auto] items-center gap-1.5">
    <span className="shrink-0 text-xs text-gray-600">{label}:</span>
    <div className="w-[128px] min-w-0 max-w-[128px] shrink">
      <SearchableDropdown
        categories={categories}
        selectedItem={selectedItem}
        onSelect={item => onSelect(item?.id ?? selectedItem.id)}
        remoteSearch
        alwaysShowSearch
        onSearchChange={onSearchChange}
        onOpenChange={onOpenChange}
        isLoading={isLoading}
        searchPlaceholder={searchPlaceholder}
        emptyMessage={emptyMessage}
        size="sm"
        triggerClassName="bg-white [&>div:first-child]:min-w-0 [&>div:first-child>span]:block [&>div:first-child>span]:min-w-0 [&>div:first-child>span]:truncate"
        className="left-0 right-auto w-[220px]"
      />
    </div>
  </div>
);

export const CategoryFooterSelect = <T extends string>({
  value,
  onValueChange,
  items,
  label = "Keys",
}: {
  value: T;
  onValueChange: (value: T) => void;
  items: { value: T; label: string }[];
  label?: string;
}) => (
  <div className="flex items-center justify-end px-6 pb-6">
    <FooterLabeledSelect label={label} value={value} onValueChange={onValueChange} items={items} />
  </div>
);
